import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import Decimal from 'decimal.js';
import { useHandHistory } from 'hooks/useHandHistory';

import { buildAvatarByName } from './avatar';
import { canRevealOwnCards, getPotTotal, getViewerCards, isUncontestedWin } from './handSummary';

// Re-exported for callers and tests that historically imported it from here.
export { canRevealOwnCards } from './handSummary';
import { formatAmount, formatMetadata, formatPot } from './format';
import { PlayByPlayList } from './PlayByPlayList';
import { PotBreakdown } from './PotBreakdown';
import { PotGroup } from './PotGroup';
import { TableSnapshot } from './TableSnapshot';
import {
  HandHistorySnapshot,
  PotGroupPlayerSnapshot,
  PotGroupSnapshot,
  SeatSnapshot,
  TableSnapshot as TableSnapshotData
} from './types';
import { withWinningCards } from './winningCards';
import { getRunResults } from './winSummary';

import styles from './HandHistory.module.scss';

const isNoPayoutGroup = (pot: PotGroupSnapshot) =>
  pot.potType === 'no_payout' || pot.name === 'No Payout';

const noPayoutScore = (player: PotGroupPlayerSnapshot) =>
  (player.badge ? 8 : 0) +
  (player.cardsRevealed ? 4 : 0) +
  (player.handRank || player.loLabel ? 2 : 0) +
  (player.net !== 0 ? 1 : 0);

const toNoPayoutPlayer = (
  seat: SeatSnapshot,
  candidates: PotGroupPlayerSnapshot[]
): PotGroupPlayerSnapshot => {
  const bestCandidate = [...candidates].sort((a, b) => noPayoutScore(b) - noPayoutScore(a))[0];

  if (bestCandidate) {
    return { ...bestCandidate, isWinner: false };
  }

  return {
    playerId: `seat-${seat.seat}`,
    name: seat.name,
    avatar: seat.avatar,
    seat: seat.seat,
    isDealer: seat.isDealer,
    folded: seat.folded,
    cards: seat.cards,
    cardsRevealed: Boolean(seat.cardsRevealed),
    net: seat.net ?? 0,
    badge: seat.folded ? 'FOLD' : undefined
  };
};

export const buildDisplayPotGroups = (hand: HandHistorySnapshot): PotGroupSnapshot[] => {
  const playersBySeat = new Map<number, PotGroupPlayerSnapshot[]>();
  const groupedSeats = new Set<number>();

  hand.potGroups.forEach((pot) => {
    pot.players.forEach((player) => {
      playersBySeat.set(player.seat, [...(playersBySeat.get(player.seat) ?? []), player]);

      if (!isNoPayoutGroup(pot)) {
        groupedSeats.add(player.seat);
      }
    });
  });

  const payoutGroups = hand.potGroups
    .filter((pot) => !isNoPayoutGroup(pot))
    .filter((pot) => pot.players.length > 0);

  const noPayoutPlayers = [...hand.tableSnapshot.seats]
    .sort((a, b) => a.seat - b.seat)
    .filter((seat) => !groupedSeats.has(seat.seat))
    .map((seat) => toNoPayoutPlayer(seat, playersBySeat.get(seat.seat) ?? []));

  if (noPayoutPlayers.length === 0) {
    return payoutGroups;
  }

  return [
    ...payoutGroups,
    {
      name: 'No Payout',
      amount: 0,
      players: noPayoutPlayers
    }
  ];
};

const buildDisplayTableSnapshot = (
  hand: HandHistorySnapshot,
  displayPotGroups: PotGroupSnapshot[]
): TableSnapshotData => {
  const seatsBySeat = new Map<number, SeatSnapshot>(
    hand.tableSnapshot.seats.map((seat) => [seat.seat, seat])
  );

  displayPotGroups.forEach((pot) => {
    pot.players.forEach((player) => {
      const seat = seatsBySeat.get(player.seat);

      seatsBySeat.set(player.seat, {
        ...seat,
        seat: player.seat,
        name: player.name,
        avatar: player.avatar ?? seat?.avatar,
        stack: seat?.stack,
        isDealer: player.isDealer ?? seat?.isDealer,
        isWinner: Boolean(seat?.isWinner || player.isWinner),
        folded: player.folded ?? seat?.folded,
        mucked: player.mucked ?? seat?.mucked,
        cards: player.cards ?? seat?.cards,
        cardsRevealed: player.cardsRevealed ?? seat?.cardsRevealed,
        net: seat?.net ?? player.net
      });
    });
  });

  const seats = Array.from(seatsBySeat.values()).sort((a, b) => a.seat - b.seat);

  return {
    ...hand.tableSnapshot,
    totalSeats: Math.max(hand.tableSnapshot.totalSeats ?? seats.length, seats.length),
    seats
  };
};

/**
 * One run of a multi-run hand as its own hand: that run's winners (every
 * other run's winning rows dropped, so a seat that won only another run lands
 * in No Payout), each pot's amount what this run paid, and that run's board.
 */
export const buildRunHand = (
  hand: HandHistorySnapshot,
  run: number,
  board: string[]
): HandHistorySnapshot => {
  const potGroups = hand.potGroups
    .map((pot) => {
      const players = pot.players.filter((player) => !player.isWinner || player.run === run);
      return isNoPayoutGroup(pot)
        ? { ...pot, players }
        : {
            ...pot,
            players,
            amount: players
              .reduce((sum, player) => sum.plus(player.grossWin ?? 0), new Decimal(0))
              .toNumber()
          };
    })
    .filter((pot) => pot.players.length > 0);

  return {
    ...hand,
    board,
    boards: undefined,
    pots: undefined,
    potGroups,
    tableSnapshot: {
      ...hand.tableSnapshot,
      board,
      seats: hand.tableSnapshot.seats.map((seat) => ({ ...seat, isWinner: false }))
    }
  };
};

/** The payout groups a snapshot can show one at a time: those with a winner. */
export const getPotSlides = (hand: HandHistorySnapshot): PotGroupSnapshot[] =>
  hand.potGroups.filter(
    (pot) => !isNoPayoutGroup(pot) && pot.players.some((player) => player.isWinner)
  );

/**
 * One pot of a hand (or of one run) as its own hand: only that pot's winners
 * are winners, its amount is the pot, every other seat is in No Payout.
 */
export const buildPotHand = (
  hand: HandHistorySnapshot,
  pot: PotGroupSnapshot
): HandHistorySnapshot => ({
  ...hand,
  pots: undefined,
  potGroups: [pot],
  tableSnapshot: {
    ...hand.tableSnapshot,
    seats: hand.tableSnapshot.seats.map((seat) => ({ ...seat, isWinner: false }))
  }
});

/** One mounted table snapshot of a hand, a run, or one pot of either. */
const HandTableSnapshot = ({ hand }: { hand: HandHistorySnapshot }) => {
  const props = useMemo(() => {
    const displayPotGroups = buildDisplayPotGroups(hand);
    return {
      snapshot: buildDisplayTableSnapshot(hand, displayPotGroups),
      potTotal: getPotTotal(hand),
      potGroups: hand.potGroups
    };
  }, [hand]);

  return (
    <TableSnapshot
      snapshot={props.snapshot}
      gameType={hand.gameType}
      gameVariant={hand.gameVariant}
      bettingStructure={hand.bettingStructure}
      metadata={hand.metadata}
      potTotal={props.potTotal}
      potGroups={props.potGroups}
    />
  );
};

/** A swipe must travel this far (px) to change the pot, so a tap never does. */
const SWIPE_DISTANCE = 40;

/**
 * A hand's (or a run's) table snapshot, one pot at a time: Main → Side 1 →
 * Side 2, each with its winner(s), amount and winning hand. Left/right arrows,
 * the arrow keys and a horizontal swipe step through them. A table with one
 * pot is drawn as before, with no arrows. Only the pot on screen is mounted —
 * the snapshot is keyed by it, so changing pot unmounts the previous one.
 *
 * Under the pot sits the whole run's (or hand's) total, every pot it paid, so
 * a run can be reconciled with the hand's Total Pot without adding up the
 * slides by hand (EN-10952: $37.97 + $37.97 + $37.96 = $113.90). A one-pot
 * table needs no such line: its felt already shows that total.
 */
const PotCarousel = ({ hand, label }: { hand: HandHistorySnapshot; label: string }) => {
  const slides = useMemo(() => getPotSlides(hand), [hand]);
  const total = useMemo(() => getPotTotal(hand), [hand]);
  const [index, setIndex] = useState(0);
  const swipeStart = useRef<number | null>(null);
  const current = Math.min(index, Math.max(0, slides.length - 1));
  const slideHand = useMemo(
    () => (slides.length > 1 ? buildPotHand(hand, slides[current]) : hand),
    [current, hand, slides]
  );

  if (slides.length <= 1) return <HandTableSnapshot hand={hand} />;

  // From the slide on screen: a refreshed hand with fewer pots clamps it, and a
  // step from the stale index past it would leave the slide unchanged.
  const go = (step: number) => setIndex(Math.max(0, Math.min(slides.length - 1, current + step)));
  const pot = slides[current];

  return (
    <div
      role='group'
      aria-label={`${label} pots`}
      tabIndex={0}
      className={styles.potCarousel}
      onKeyDown={(event) => {
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          go(event.key === 'ArrowLeft' ? -1 : 1);
        }
      }}
      onTouchStart={(event) => {
        swipeStart.current = event.changedTouches[0]?.clientX ?? null;
      }}
      onTouchEnd={(event) => {
        const start = swipeStart.current;
        const end = event.changedTouches[0]?.clientX;
        swipeStart.current = null;
        if (start === null || end === undefined || Math.abs(end - start) < SWIPE_DISTANCE) return;
        go(end < start ? 1 : -1);
      }}>
      <div className={styles.potCarouselBar}>
        <button
          type='button'
          className={styles.potCarouselArrow}
          aria-label='Previous pot'
          disabled={current === 0}
          onClick={() => go(-1)}>
          <KeyboardArrowLeftIcon />
        </button>
        <span className={styles.potCarouselHeading}>
          <span className={styles.potCarouselTitle} aria-live='polite'>
            <strong>{pot.name}</strong>
            <span>{formatPot(pot.amount, hand.gameType)}</span>
            <span className={styles.potCarouselCount}>{`${current + 1} / ${slides.length}`}</span>
          </span>
          <span className={styles.potCarouselTotal} data-testid='pot-carousel-total'>
            {`${label} total `}
            <strong>{formatPot(total, hand.gameType)}</strong>
          </span>
        </span>
        <button
          type='button'
          className={styles.potCarouselArrow}
          aria-label='Next pot'
          disabled={current === slides.length - 1}
          onClick={() => go(1)}>
          <KeyboardArrowRightIcon />
        </button>
      </div>
      <HandTableSnapshot key={current} hand={slideHand} />
    </div>
  );
};

/**
 * One table snapshot per run of a multi-run hand, each a carousel of that
 * run's pots. Each mounts a whole table with its own store, so only the one
 * being viewed is mounted: opening a run closes the other, and closing it
 * unmounts it.
 */
const RunSnapshots = ({ hand, boards }: { hand: HandHistorySnapshot; boards: string[][] }) => {
  const [openRun, setOpenRun] = useState<number | null>(null);
  // A refreshed hand can carry fewer runs than the one open: close it, so it
  // neither builds a run with no board nor reopens if the run comes back.
  const isOpenRunGone = openRun !== null && openRun > boards.length;
  if (isOpenRunGone) setOpenRun(null);
  const runHand = useMemo(
    () =>
      openRun === null || isOpenRunGone
        ? undefined
        : buildRunHand(hand, openRun, boards[openRun - 1]),
    [boards, hand, isOpenRunGone, openRun]
  );

  return (
    <>
      {boards.map((_, index) => {
        const run = index + 1;
        const isOpen = openRun === run;
        return (
          <section key={run} className={styles.snapshotSection}>
            <button
              type='button'
              className={styles.snapshotToggle}
              aria-expanded={isOpen}
              onClick={() => setOpenRun((value) => (value === run ? null : run))}>
              <span>{`Table Snapshot · Run ${run}`}</span>
              <KeyboardArrowDownIcon className={isOpen ? styles.toggleOpen : ''} />
            </button>
            {isOpen && runHand && <PotCarousel key={run} hand={runHand} label={`Run ${run}`} />}
          </section>
        );
      })}
    </>
  );
};

/**
 * The expanded body of a completed hand's card. The card header already shows
 * the hand number, time, hole cards, board, pot and net, so this goes: blinds,
 * the result, how the money was split, the frozen table, then every action
 * (newest first) - Jason, 2026-09-23.
 */
export const HandSnapshot = ({
  hand,
  presentation
}: {
  hand: HandHistorySnapshot;
  /** Playground composition only; the live panel keeps its default presentation. */
  presentation?: 'embedded' | 'results-first';
}) => {
  const { revealOwnCards } = useHandHistory();
  // The shipping panel opens a hand on its play-by-play and keeps the No Payout
  // list folded (Jason, 28 Sep); the playground's comparison layouts keep the
  // defaults they were built with.
  const isLivePanel = !presentation;
  const [isPlayByPlayOpen, setIsPlayByPlayOpen] = useState(isLivePanel);
  // Collapsed by default (design review 2026-08-20): the snapshot is the
  // heaviest thing in the panel and most questions are answered above it.
  const [isSnapshotOpen, setIsSnapshotOpen] = useState(false);
  const displayPotGroups = useMemo(() => buildDisplayPotGroups(hand), [hand]);
  const displaySnapshot = useMemo(
    () => buildDisplayTableSnapshot(hand, displayPotGroups),
    [displayPotGroups, hand]
  );
  useEffect(() => {
    const viewerSeat = hand.tableSnapshot.viewerSeat;
    if (!canRevealOwnCards(viewerSeat, displaySnapshot.seats)) return;
    revealOwnCards(hand.gameNo, hand.completedAt);
  }, [
    displaySnapshot.seats,
    hand.completedAt,
    hand.gameNo,
    hand.tableSnapshot.viewerSeat,
    revealOwnCards
  ]);

  // Seat → avatar filename for the payout rows (undefined → the row shows the
  // player's initial, like Game Chat). Name → avatar for the play-by-play rows.
  const avatarBySeat = useMemo(
    () =>
      displaySnapshot.seats.reduce<Record<number, string | undefined>>((avatars, seat) => {
        avatars[seat.seat] = seat.avatar;
        return avatars;
      }, {}),
    [displaySnapshot.seats]
  );
  // The pre-flop round's header shows what the viewer held, once their own
  // cards are back in the snapshot (see useOwnCardsRecovery).
  const viewerCards = getViewerCards(hand);
  const viewerHoleCards = viewerCards.revealed ? viewerCards.cards : undefined;
  // A multi-run hand gets a snapshot per run and a summary per run header.
  const runBoards = hand.boards && hand.boards.length > 1 ? hand.boards : undefined;
  // Each snapshot's winners show the five they played on that board, never all
  // seven cards; a win nobody had to show for lights nothing.
  const litHand = useMemo(() => (isUncontestedWin(hand) ? hand : withWinningCards(hand)), [hand]);
  // The payout rows too: each winner's row shows the board cards of the five
  // they played on that run, so every run needs its five, not only the last.
  const payoutPotGroups = useMemo(() => buildDisplayPotGroups(litHand), [litHand]);
  const boardOf = useCallback(
    (run?: number) => (run ? hand.boards?.[run - 1] : undefined) ?? hand.board,
    [hand.board, hand.boards]
  );
  const runResults = useMemo(() => getRunResults(hand), [hand]);
  const avatarByName = useMemo(
    () =>
      buildAvatarByName(
        displaySnapshot.seats.map((seat) => ({ name: seat.name, avatar: seat.avatar }))
      ),
    [displaySnapshot.seats]
  );

  return (
    <div
      className={`${styles.handSnapshot} ${presentation ? styles.embeddedSnapshot : ''} ${
        presentation === 'results-first' ? styles.resultsFirst : ''
      }`}>
      <div className={styles.handSnapshotBody}>
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <span>{hand.gameType === 'tournament' ? 'Level' : 'Blinds'}</span>
            <strong>{formatMetadata(hand.metadata, hand.gameType)}</strong>
          </div>
        </section>

        {hand.viewerResult && (
          <section className={styles.handResultSummary}>
            <span className={styles.handResultLabel}>Hand Result</span>
            <div className={styles.handResultValue}>
              <span>
                {hand.viewerResult.outcome === 'won'
                  ? 'You won'
                  : hand.viewerResult.outcome === 'lost'
                  ? 'You lost'
                  : 'Break even'}
              </span>
              <strong>{formatAmount(hand.viewerResult.net, hand.gameType)}</strong>
            </div>
            <span className={styles.handResultMeta}>Net P&amp;L</span>
            {hand.viewerResult.scoop && <span className={styles.scoopBadge}>Scoop</span>}
          </section>
        )}

        {litHand.pots?.length
          ? litHand.pots.map((pot) => (
              <PotBreakdown
                key={pot.potId}
                pot={pot}
                gameType={hand.gameType}
                viewerSeat={hand.tableSnapshot.viewerSeat}
                avatarBySeat={avatarBySeat}
                boardOf={boardOf}
              />
            ))
          : payoutPotGroups.map((pot) => (
              <PotGroup
                key={pot.name}
                pot={pot}
                gameType={hand.gameType}
                viewerSeat={hand.tableSnapshot.viewerSeat}
                avatarBySeat={avatarBySeat}
                boardOf={boardOf}
                showAmount={pot.name !== 'No Payout'}
                collapsible={pot.name === 'No Payout'}
                defaultOpen={pot.name !== 'No Payout' || !isLivePanel}
              />
            ))}

        {runBoards ? (
          <RunSnapshots hand={litHand} boards={runBoards} />
        ) : (
          <section className={styles.snapshotSection}>
            <button
              type='button'
              className={styles.snapshotToggle}
              aria-expanded={isSnapshotOpen}
              onClick={() => setIsSnapshotOpen((current) => !current)}>
              <span>Table Snapshot</span>
              <KeyboardArrowDownIcon className={isSnapshotOpen ? styles.toggleOpen : ''} />
            </button>
            {isSnapshotOpen && <PotCarousel hand={litHand} label='Hand' />}
          </section>
        )}

        <section className={styles.playByPlaySection}>
          <button
            type='button'
            className={styles.playByPlayToggle}
            aria-expanded={isPlayByPlayOpen}
            onClick={() => setIsPlayByPlayOpen((current) => !current)}>
            <span>Play-by-play</span>
            <strong>{hand.playByPlay.length} events</strong>
            <KeyboardArrowDownIcon className={isPlayByPlayOpen ? styles.toggleOpen : ''} />
          </button>
          {isPlayByPlayOpen && (
            <PlayByPlayList
              events={hand.playByPlay}
              gameType={hand.gameType}
              avatarByName={avatarByName}
              showTableCards={Boolean(presentation)}
              holeCards={viewerHoleCards}
              runResults={runResults}
            />
          )}
        </section>
      </div>
    </div>
  );
};
